import { forwardRef } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'

interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'size'> {
  /** Optional UPPERCASE-tracked label above the input. */
  label?: string
  /** Required marker (renders an asterisk in the label). */
  required?: boolean
  /** Inline error message below the field. Sets aria-invalid + rose border. */
  error?: ReactNode
  /** Compact size — for inline filters/search. `lg` = 18 px text, 48 px tall (Schulportal, DESIGN.md §17). */
  size?: 'default' | 'compact' | 'lg'
  /** `surface` (default) on flat pages; `glass` for fields placed on Liquid Glass surfaces (modals, palette, glass filter rows). */
  variant?: 'surface' | 'glass'
  /** Monospace + letter-spaced + tabular digits — for code/scanner/readout fields. */
  mono?: boolean
  /** Text alignment of the input value (default left). */
  align?: 'left' | 'center'
  /** Large display value in the heading font (32 px, extra bold, wide tracking) — Schulportal birth-year field (prototype .year-in). */
  display?: boolean
  /** Material symbol name rendered inside the field on the left (adds left padding). */
  leadingIcon?: string
}

/**
 * The single canonical text input. NEVER style <input> per-page. See DESIGN.md § 8c + § 14.
 *
 * Use type="number"/"date"/"email" via the standard HTML attribute.
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput({ label, required, error, size = 'default', variant = 'surface', mono, align, display, leadingIcon, id, ...rest }, ref) {
  const fieldId = id || (label ? `tf-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined)
  const surfaceFill = size === 'compact'
    ? 'bg-surface-container-low text-on-surface placeholder:text-on-surface-variant px-2.5 py-1.5 rounded-lg text-xs focus:bg-surface-container'
    : size === 'lg'
      ? 'bg-surface-container-low text-on-surface placeholder:text-on-surface-variant min-h-12 px-4 py-2.5 rounded-xl text-lg focus:bg-surface-container'
      : 'bg-surface-container-low text-on-surface placeholder:text-on-surface-variant px-3 py-2.5 rounded-xl text-base sm:text-sm focus:bg-surface-container'
  const glassFill = size === 'compact'
    ? 'bg-white/50 dark:bg-slate-900/45 backdrop-blur text-on-surface placeholder:text-on-surface-variant px-2.5 py-1.5 rounded-lg text-xs focus:bg-white/85 dark:focus:bg-slate-900/75'
    : size === 'lg'
      ? 'bg-white/50 dark:bg-slate-900/45 backdrop-blur text-on-surface placeholder:text-on-surface-variant min-h-12 px-4 py-2.5 rounded-xl text-lg focus:bg-white/85 dark:focus:bg-slate-900/75'
      : 'bg-white/50 dark:bg-slate-900/45 backdrop-blur text-on-surface placeholder:text-on-surface-variant px-3 py-2.5 rounded-xl text-base sm:text-sm focus:bg-white/85 dark:focus:bg-slate-900/75'
  const baseInput = `w-full border outline-none transition-colors ${variant === 'glass' ? glassFill : surfaceFill}`
  const stateBorder = error
    ? 'border-rose-300 focus:border-rose-500'
    : variant === 'glass'
      ? 'border-white/50 dark:border-white/15 focus:border-primary/60'
      : 'border-outline-variant focus:border-primary'
  const extra = [
    mono ? 'font-mono tracking-[0.2em] tabular-nums' : '',
    display ? 'font-headline text-[32px] leading-[1.45] font-extrabold tracking-[0.3em] tabular-nums !bg-card !rounded-[14px] !px-3.5 !py-2.5' : '',
    align === 'center' ? 'text-center' : '',
    leadingIcon ? 'pl-10' : '',
  ].filter(Boolean).join(' ')

  // A caller's own aria-invalid (e.g. a field that validates itself without the error prop) must win
  // over the error-derived default, never be overwritten by it.
  const ariaInvalid = rest['aria-invalid'] ?? (!!error || undefined)
  const inputEl = <input ref={ref} id={fieldId} {...rest} aria-invalid={ariaInvalid} className={`${baseInput} ${stateBorder} ${extra}`} />

  return (
    <div className={size === 'compact' ? '' : 'space-y-1.5'}>
      {label && (
        <label htmlFor={fieldId} className="block text-[10px] font-medium uppercase tracking-wider text-on-surface-variant">
          {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
        </label>
      )}
      {leadingIcon ? (
        <div className="relative">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none !text-[20px]" aria-hidden="true">{leadingIcon}</span>
          {inputEl}
        </div>
      ) : inputEl}
      {error && <p className="text-[11px] text-rose-600">{error}</p>}
    </div>
  )
})

interface TextAreaProps extends Omit<InputHTMLAttributes<HTMLTextAreaElement>, 'className' | 'size'> {
  label?: string
  rows?: number
  error?: ReactNode
  variant?: 'surface' | 'glass'
  /** `lg` = 18 px text (Schulportal, DESIGN.md §17). */
  size?: 'default' | 'lg'
}

export function TextArea({ label, rows = 3, error, variant = 'surface', size = 'default', id, ...rest }: TextAreaProps) {
  const fieldId = id || (label ? `ta-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined)
  const fill = variant === 'glass'
    ? 'bg-white/50 dark:bg-slate-900/45 backdrop-blur text-on-surface placeholder:text-on-surface-variant focus:bg-white/85 dark:focus:bg-slate-900/75'
    : 'bg-surface-container-low text-on-surface placeholder:text-on-surface-variant focus:bg-surface-container'
  const stateBorder = error
    ? 'border-rose-300 focus:border-rose-500'
    : variant === 'glass'
      ? 'border-white/50 dark:border-white/15 focus:border-primary/60'
      : 'border-outline-variant focus:border-primary'
  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={fieldId} className="block text-[10px] font-medium uppercase tracking-wider text-on-surface-variant">
          {label}
        </label>
      )}
      <textarea id={fieldId} rows={rows} {...rest} aria-invalid={!!error || undefined}
        className={`w-full ${fill} border px-3 py-2.5 rounded-xl ${size === 'lg' ? 'text-lg' : 'text-sm'} outline-none transition-colors resize-none ${stateBorder}`} />
      {error && <p className="text-[11px] text-rose-600">{error}</p>}
    </div>
  )
}
